import { useState } from "react";
import { z } from "zod";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

const schema = z.object({
  email: z.string().trim().email("Please enter a valid email address").max(255),
  consent: z.literal(true, {
    errorMap: () => ({ message: "Please confirm consent to subscribe." }),
  }),
});

interface Props {
  source: string;
  className?: string;
}

const NewsletterInline = ({ source, className = "" }: Props) => {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || done) return;

    const parsed = schema.safeParse({ email, consent });
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message ?? "Invalid input");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("subscribe-newsletter", {
        body: { email: parsed.data.email, source },
      });
      if (error) throw error;
      setDone(true);
      toast.success("Check your inbox to confirm your subscription.");
      try {
        trackEvent("blog_newsletter_subscribe", { source });
      } catch { /* no-op */ }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <aside
      className={`my-10 rounded-2xl border border-border bg-card p-6 md:p-8 ${className}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-bold text-lg text-foreground">
            One investing email, no spam.
          </h3>
          <p className="text-sm text-foreground/65">
            New broker comparisons, portfolio strategy, and AI investing — direct to your inbox.
          </p>
        </div>
      </div>

      {done ? (
        <div className="flex items-center gap-2 text-primary mt-4 text-sm font-medium">
          <CheckCircle2 className="w-5 h-5" />
          Thanks — please check your inbox to confirm.
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3 mt-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={255}
              className="flex-1 rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground px-5 py-3 text-sm font-semibold hover:opacity-90 transition disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe"}
            </button>
          </div>
          <label className="flex items-start gap-2 text-xs text-foreground/65">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5"
              required
            />
            <span>
              I agree to receive emails from PieFolio AI and accept the{" "}
              <a href="/privacy" className="text-primary underline">
                privacy policy
              </a>
              . Unsubscribe anytime.
            </span>
          </label>
        </form>
      )}
    </aside>
  );
};

export default NewsletterInline;
